/**
 * Petty Cash Voucher — a real voucher for petty cash movements: Draft → Submitted → Approved →
 * Posted, its own document series (PCV-YYYY-NNNNNN), maker-checker, print, and reversal.
 *
 * Replaces the old Top-up / Pay / Return modals, which moved cash the instant a button was
 * clicked — no draft, no second pair of eyes, no document to print or reverse.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Send, CheckCircle2, XCircle, BookCheck, RotateCcw, Ban,
  AlertTriangle, CheckCircle, FileText, ShieldCheck, Printer, ExternalLink,
  Wallet, ArrowDownCircle, ArrowUpCircle, Undo2, Plus, Trash2,
} from 'lucide-react';

import { pettyCashVoucherApi } from '@/api/pettyCashVoucher.api';
import { pettyCashApi } from '@/api/pettyCash.api';
import { AccountLookupField } from '@/components/erp/AccountLookupField';
import { AuditViewer } from '@/components/erp/AuditViewer';
import { PettyCashVoucherPrint } from '@/components/pty/PettyCashVoucherPrint';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { cn } from '@/utils/cn';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { RootState } from '@/state/store';

import type { PettyCashFloatListDto } from '@/types/pettyCash.types';
import type {
  PettyCashVoucherDto, PettyCashMovementType, PettyCashWorkflowStatus,
} from '@/types/pettyCashVoucher.types';

// ── Movement type meta ───────────────────────────────────────────────────────

const MOVEMENT_META: Record<PettyCashMovementType, {
  label: string; verb: string; accountLabel: string; icon: typeof ArrowDownCircle; color: string;
}> = {
  TOPUP:   { label: 'Top-up',  verb: 'tops up',  accountLabel: 'Funding account (where the cash comes from)',
             icon: ArrowDownCircle, color: 'text-emerald-600' },
  PAYMENT: { label: 'Payment', verb: 'pays',     accountLabel: 'Expense account (what this pays for)',
             icon: ArrowUpCircle, color: 'text-red-600' },
  RETURN:  { label: 'Return',  verb: 'returns',  accountLabel: 'Funding account (where the cash goes back to)',
             icon: Undo2, color: 'text-amber-600' },
};

const STATUS_COLORS: Record<PettyCashWorkflowStatus, string> = {
  Draft:              'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
  Submitted:          'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  PendingApproval:    'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  PartiallyApproved:  'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  Approved:           'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  Rejected:           'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
  Cancelled:          'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
  Escalated:          'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  Posted:             'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
};

const TABS = [
  { key: 'details', label: 'Details',  icon: FileText },
  { key: 'audit',   label: 'Audit Log', icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Action modal (reject / cancel / reverse — all need a reason) ────────────

interface ActionModalProps {
  title: string; placeholder: string; required: boolean;
  onConfirm: (text: string) => void; onCancel: () => void;
}
const ActionModal = ({ title, placeholder, required, onConfirm, onCancel }: ActionModalProps) => {
  const [text, setText] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-5 shadow-xl">
        <h3 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder={placeholder}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900
                     px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(text)}
            disabled={required && !text.trim()}
            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const fieldClass = (disabled: boolean) => cn(
  'w-full h-9 px-3 rounded-md border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
  'border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500',
  disabled && 'bg-gray-50 dark:bg-gray-900 text-gray-500 cursor-not-allowed',
);

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * One expense row on a payment (ML-13). Amounts are held as strings because the input is text
 * until it is saved — an empty box is not zero, and treating it as zero silently changes what the
 * operator typed.
 */
interface ExpenseLineRow {
  key:         string;
  accountId:   string | null;
  accountCode: string | null;
  accountName: string | null;
  amount:      string;
  description: string;
  /** Overrides the voucher's cost centre for this expense. Empty takes the voucher's. */
  costCenterId: string;
}

const blankExpenseLine = (): ExpenseLineRow => ({
  key:         `line-${Date.now()}-${Math.random()}`,
  accountId:   null,
  accountCode: null,
  accountName: null,
  amount:      '',
  description: '',
  costCenterId: '',
});

// ── "Keep as my default" cost centre (424359) ──────────────────────────────
// Per user and float, in this browser: a cashier who always books a tin's spending to the same cost
// centre picks it once. Best-effort — without storage the voucher simply falls back to the session's.
const defaultCcKey = (userId: string) => `pty.defaultCostCentre.${userId}`;

const readDefaultCcs = (userId: string): Record<string, string> => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(defaultCcKey(userId)) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const writeDefaultCc = (userId: string, floatId: string, costCenterId: string | null) => {
  try {
    const all = readDefaultCcs(userId);
    if (costCenterId) all[floatId] = costCenterId; else delete all[floatId];
    localStorage.setItem(defaultCcKey(userId), JSON.stringify(all));
  } catch {
    /* storage unavailable — the default is a convenience only */
  }
};

export const PettyCashVoucherFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.PTY_VOUCHER_EDIT);
  const canSubmit  = hasPermission(Permissions.PTY_VOUCHER_SUBMIT);
  const canApprove = hasPermission(Permissions.PTY_VOUCHER_APPROVE);
  const canReject  = hasPermission(Permissions.PTY_VOUCHER_REJECT);
  const canPost    = hasPermission(Permissions.PTY_VOUCHER_POST);
  const canCancel  = hasPermission(Permissions.PTY_VOUCHER_CANCEL);
  const canReverse = hasPermission(Permissions.PTY_VOUCHER_REVERSE);
  const canPrint   = hasPermission(Permissions.PTY_VOUCHER_PRINT);

  const [voucher, setVoucher] = useState<PettyCashVoucherDto | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('details');
  const [modal, setModal] = useState<'reject' | 'cancel' | 'reverse' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  const [floats, setFloats] = useState<PettyCashFloatListDto[]>([]);

  // ── Header fields ────────────────────────────────────────────────────────
  const [floatId,      setFloatId]      = useState('');
  const [movementType, setMovementType] = useState<PettyCashMovementType>(
    (searchParams.get('type') as PettyCashMovementType) || 'PAYMENT');
  const [voucherDate,  setVoucherDate]  = useState(todayIso());
  const [postingDate,  setPostingDate]  = useState(todayIso());
  const [amount,       setAmount]       = useState('');

  /**
   * The expenses a PAYMENT itemises (ML-13). A top-up or a return moves money against one funding
   * account and keeps using the single Amount and account fields above.
   */
  const [lines, setLines] = useState<ExpenseLineRow[]>([blankExpenseLine()]);
  const [counterId,    setCounterId]    = useState<string | null>(null);
  const [counterCode,  setCounterCode]  = useState<string | null>(null);
  const [counterName,  setCounterName]  = useState<string | null>(null);
  // Petty cash was the one transaction with no cost centre at all — the screen had no selector
  // and the journal it posts hardcoded null, so every petty cash expense sat outside cost-centre
  // reporting (QC Part III item 8). Defaulted from the session scope, as the other voucher screens
  // do, so the ordinary case needs no thought.
  const session               = useSelector((s: RootState) => s.session.session);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);
  const userId                = useSelector((s: RootState) => s.auth.user?.userId ?? '');
  const [costCenterId, setCostCenterId] = useState('');
  const [keepDefault,  setKeepDefault]  = useState(false);

  /** The float decides the location, so the centres offered follow the float, not the session. */
  const floatLocationId = floats.find(f => f.id === floatId)?.locationId ?? session?.locationId ?? null;

  // A new voucher opens on the user's kept default for this float, else the scope they are working
  // in; an existing one keeps what it saved. A kept default no longer offered here is ignored.
  useEffect(() => {
    if (!isNew) return;
    const kept = floatId && userId ? readDefaultCcs(userId)[floatId] : undefined;
    const usable = kept && costCentresForLocation(accessibleCostCenters, floatLocationId).some(cc => cc.id === kept);
    setCostCenterId(usable ? kept : session?.costCenterId ?? '');
    setKeepDefault(!!usable);
  }, [isNew, floatId, userId, floatLocationId, accessibleCostCenters, session?.costCenterId]);

  const changeCostCenter = (value: string) => {
    setCostCenterId(value);
    if (keepDefault && floatId && userId) writeDefaultCc(userId, floatId, value || null);
  };
  const toggleKeepDefault = (keep: boolean) => {
    setKeepDefault(keep);
    if (floatId && userId) writeDefaultCc(userId, floatId, keep ? costCenterId || null : null);
  };
  const voucherCcCode = accessibleCostCenters.find(cc => cc.id === costCenterId)?.code;
  const [reference,    setReference]    = useState('');
  const [narration,    setNarration]    = useState('');

  // A payment's amount is the sum of what it itemises, so the two cannot disagree (ML-13).
  const isItemised   = movementType === 'PAYMENT';
  const linesTotal   = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

  const updateLine = (key: string, patch: Partial<ExpenseLineRow>) =>
    setLines(ls => ls.map(l => (l.key === key ? { ...l, ...patch } : l)));

  const addLine    = () => setLines(ls => [...ls, blankExpenseLine()]);
  const removeLine = (key: string) =>
    setLines(ls => (ls.length === 1 ? ls : ls.filter(l => l.key !== key)));

  const status = voucher?.workflowStatus ?? 'Draft';
  const isEditable = isNew || (status === 'Draft' && canEdit);
  const selectedFloat = floats.find(f => f.id === floatId) ?? null;
  const meta = MOVEMENT_META[movementType];

  // ── Load floats for the picker ───────────────────────────────────────────
  useEffect(() => {
    // Only the floats reachable from where this user is working. A branch tin is cash that sits
    // at that branch; offering it at head office invites a payment nobody there can hand over.
    pettyCashApi.list('ACTIVE', session?.locationId || undefined)
      .then(r => setFloats(r.data?.data ?? [])).catch(() => {});
  }, [session?.locationId]);

  // ── Seed float from ?float= query param (arriving from the Floats screen) ─
  useEffect(() => {
    if (isNew) {
      const f = searchParams.get('float');
      if (f) setFloatId(f);
    }
  }, [isNew, searchParams]);

  // ── Load an existing voucher ─────────────────────────────────────────────
  const load = useCallback(() => {
    if (isNew || !id) return;
    setLoading(true);
    pettyCashVoucherApi.getById(id)
      .then(r => {
        const v = r.data.data;
        if (!v) return;
        setVoucher(v);
        setFloatId(v.floatId);
        setMovementType(v.movementType);
        setVoucherDate(v.voucherDate.slice(0, 10));
        setPostingDate(v.postingDate.slice(0, 10));
        setAmount(String(v.amount));
        setCounterId(v.counterGlAccountId);

        // A payment saved before it could be itemised opens as a single line, so every payment
        // looks the same on screen and re-saving simply records what was always true.
        if (v.movementType === 'PAYMENT') {
          setLines(
            v.lines && v.lines.length > 0
              ? v.lines.map(l => ({
                  key:         l.id,
                  accountId:   l.expenseGlAccountId,
                  accountCode: l.expenseGlAccountCode,
                  accountName: l.expenseGlAccountName,
                  amount:      String(l.amount),
                  description: l.description ?? '',
                  costCenterId: l.costCenterId ?? '',
                }))
              : [{
                  key:         `existing-${v.id}`,
                  accountId:   v.counterGlAccountId,
                  accountCode: v.counterGlAccountCode,
                  accountName: v.counterGlAccountName,
                  amount:      String(v.amount),
                  description: '',
                  costCenterId: '',
                }],
          );
        }
        setCounterCode(v.counterGlAccountCode);
        setCounterName(v.counterGlAccountName);
        setCostCenterId(v.costCenterId ?? '');
        setReference(v.reference ?? '');
        setNarration(v.narration ?? '');
      })
      .catch(() => setError('Failed to load voucher.'))
      .finally(() => setLoading(false));
  }, [isNew, id]);

  useEffect(() => { load(); }, [load]);

  const flash = (type: 'success' | 'error', text: string) => {
    if (type === 'success') { setSuccess(text); setError(null); } else { setError(text); setSuccess(null); }
    setTimeout(() => { setSuccess(null); setError(null); }, 4000);
  };

  // ── Save (create or update) ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!floatId) { flash('error', 'Choose a float.'); return; }

    // A payment is checked against its lines; a top-up or return against its single account.
    // Built with flatMap so the account narrows to a string here rather than being asserted
    // non-null at each use below.
    const expenses = isItemised
      ? lines.flatMap(l =>
          l.accountId && Number(l.amount) > 0
            ? [{ accountId: l.accountId, amount: Number(l.amount), description: l.description,
                costCenterId: l.costCenterId }]
            : [])
      : [];

    if (isItemised) {
      if (expenses.length === 0) {
        flash('error', 'Add at least one expense line with an account and an amount.');
        return;
      }
      if (lines.some(l => Number(l.amount) > 0 && !l.accountId)) {
        flash('error', 'Every expense line needs an account.');
        return;
      }
    } else if (!counterId) {
      flash('error', `Choose the ${meta.accountLabel.toLowerCase()}.`);
      return;
    }

    // The account the voucher itself names: the first expense on a payment, the chosen funding
    // account otherwise. Resolved once so neither branch has to assert it is present.
    const headerAccountId = isItemised ? expenses[0]?.accountId : counterId;
    if (!headerAccountId) { flash('error', `Choose the ${meta.accountLabel.toLowerCase()}.`); return; }

    const amt = isItemised ? linesTotal : Number(amount);
    if (!(amt > 0)) { flash('error', 'Enter an amount greater than zero.'); return; }

    setSaving(true);
    try {
      const body = {
        floatId, movementType,
        voucherDate: new Date(voucherDate).toISOString(),
        postingDate: new Date(postingDate).toISOString(),
        amount: amt,
        // The server still wants a header account and derives its own from the lines; sending the
        // first line's keeps an itemised voucher readable to anything not yet taught about lines.
        counterGlAccountId: headerAccountId,
        reference: reference || null, narration: narration || null,
        costCenterId: costCenterId || null,
        lines: isItemised
          ? expenses.map(l => ({
              expenseGlAccountId: l.accountId,
              amount:             l.amount,
              description:        l.description || null,
              costCenterId:       l.costCenterId || null,
            }))
          : null,
      };
      if (isNew) {
        const res = await pettyCashVoucherApi.create(body);
        const newId = res.data.data?.id;
        flash('success', 'Voucher created.');
        if (newId) setTimeout(() => navigate(`${AppRoutes.PETTY_CASH_VOUCHERS}/${newId}`), 600);
      } else if (voucher) {
        await pettyCashVoucherApi.update(voucher.id, body);
        flash('success', 'Voucher updated.');
        load();
      }
    } catch (e: unknown) {
      flash('error', extractError(e));
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow actions ──────────────────────────────────────────────────────
  const runAction = async (fn: () => Promise<unknown>, successMsg: string) => {
    if (!voucher) return;
    setSaving(true);
    try {
      await fn();
      flash('success', successMsg);
      load();
    } catch (e: unknown) {
      flash('error', extractError(e));
    } finally {
      setSaving(false);
      setModal(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(AppRoutes.PETTY_CASH_VOUCHERS)}
            className="flex items-center gap-1 text-sm text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-200">
            <ArrowLeft size={15} /> Back
          </button>

          {(isNew || status === 'Draft') && (canEdit || isNew) && (
            <button onClick={handleSave} disabled={saving || !isEditable}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-800
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Save size={14} /> {isNew ? 'Create' : 'Save'}
            </button>
          )}
          {!isNew && status === 'Draft' && canSubmit && (
            <button onClick={() => runAction(() => pettyCashVoucherApi.submit(voucher!.id), 'Voucher submitted.')}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Send size={14} /> Submit
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove && (
            <button onClick={() => runAction(() => pettyCashVoucherApi.approve(voucher!.id), 'Voucher approved.')}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <CheckCircle2 size={14} /> Approve
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject && (
            <button onClick={() => setModal('reject')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <XCircle size={14} /> Reject
            </button>
          )}
          {!isNew && status === 'Approved' && canPost && (
            <button onClick={() => runAction(() => pettyCashVoucherApi.post(voucher!.id), 'Voucher posted.')}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <BookCheck size={14} /> Post
            </button>
          )}
          {!isNew && status === 'Posted' && canReverse && !voucher?.reversalVoucherId && (
            <button onClick={() => setModal('reverse')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <RotateCcw size={14} /> Reverse
            </button>
          )}
          {!isNew && !['Posted', 'Cancelled'].includes(status) && canCancel && (
            <button onClick={() => setModal('cancel')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Ban size={14} /> Cancel
            </button>
          )}
          {!isNew && voucher && status === 'Posted' && canPrint && (
            <button onClick={() => setPrintOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <Printer size={14} /> Print
            </button>
          )}

          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              {voucher?.isReversal && (
                <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-full">
                  Reversal
                </span>
              )}
              <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', STATUS_COLORS[status])}>
                {status}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={14} className="shrink-0" /><span>{success}</span>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Wallet size={18} className={meta.color} />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {isNew ? `New Petty Cash ${meta.label}` : `Petty Cash Voucher ${voucher?.voucherNumber ?? ''}`}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && voucher?.originalVoucherId && (
              <button onClick={() => navigate(`${AppRoutes.PETTY_CASH_VOUCHERS}/${voucher.originalVoucherId}`)}
                className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                View Original <ExternalLink size={10} />
              </button>
            )}
            {!isNew && voucher?.reversalVoucherId && (
              <button onClick={() => navigate(`${AppRoutes.PETTY_CASH_VOUCHERS}/${voucher.reversalVoucherId}`)}
                className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                View Reversal <ExternalLink size={10} />
              </button>
            )}
            {!isNew && voucher?.sourceGlJournalId && (
              <button onClick={() => navigate(`/gl/journals/${voucher.sourceGlJournalId}`)}
                className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                View GL Journal <ExternalLink size={10} />
              </button>
            )}
          </div>
        </div>
        {!isNew && voucher && (
          <p className="mt-1 text-xs text-slate-400">Created {new Date(voucher.createdDate).toLocaleString()}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
        <div className="flex">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300',
              )}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : tab === 'details' ? (
          <div className="p-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">

              {/* Movement type picker — locked once the voucher exists */}
              <div className="mb-5">
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                  Movement Type <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {(Object.keys(MOVEMENT_META) as PettyCashMovementType[]).map(t => {
                    const m = MOVEMENT_META[t];
                    const Icon = m.icon;
                    return (
                      <button key={t} type="button"
                        onClick={() => isEditable && setMovementType(t)}
                        disabled={!isEditable}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                          movementType === t
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-gray-400',
                          !isEditable && 'opacity-60 cursor-not-allowed',
                        )}>
                        <Icon size={14} /> {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Float */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Float <span className="text-red-500">*</span>
                  </label>
                  <select value={floatId} onChange={e => setFloatId(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    <option value="">— Select Float —</option>
                    {floats.map(f => (
                      <option key={f.id} value={f.id}>{f.floatCode} — {f.floatName}</option>
                    ))}
                  </select>
                  {selectedFloat && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      Balance now: <span className="font-mono">{selectedFloat.currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> {selectedFloat.currencyCode}
                      {selectedFloat.custodian ? ` · ${selectedFloat.custodian}` : ''}
                    </p>
                  )}
                </div>

                {/* Voucher Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Voucher Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={voucherDate} onChange={e => setVoucherDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Posting Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Posting Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={postingDate} onChange={e => setPostingDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Amount — typed for a top-up or return, totalled from the lines for a payment. */}
                {!isItemised && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                      disabled={!isEditable} placeholder="0.00" className={cn(fieldClass(!isEditable), 'font-mono')} />
                  </div>
                )}

                {/* Counter account — a payment names its accounts on the lines below instead. */}
                {!isItemised && (
                  <div className="lg:col-span-2">
                    <AccountLookupField
                      label={`${meta.accountLabel} *`}
                      value={counterId} displayCode={counterCode} displayName={counterName}
                      onChange={(cid, code, name) => { setCounterId(cid); setCounterCode(code); setCounterName(name); }}
                      disabled={!isEditable}
                      isPostingAccount
                      placeholder={isEditable ? 'Click to select account' : '—'}
                    />
                  </div>
                )}

                {/* Total, so a payment still shows its figure where the Amount box used to be. */}
                {isItemised && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Total
                    </label>
                    <div className={cn(fieldClass(true), 'font-mono flex items-center')}>
                      {linesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">Totalled from the expenses below.</p>
                  </div>
                )}

                {/* Cost Centre (QC Part III item 8) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Cost Center
                  </label>
                  <select value={costCenterId} onChange={e => changeCostCenter(e.target.value)}
                    aria-label="Cost Center"
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    <option value="">— None —</option>
                    {costCentresForLocation(accessibleCostCenters, floatLocationId).map(cc => (
                      <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Where this spending is reported. An expense line may name its own.
                  </p>
                  {isEditable && floatId && userId && (
                    <label className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer">
                      <input type="checkbox" checked={keepDefault} disabled={!costCenterId}
                        onChange={e => toggleKeepDefault(e.target.checked)} />
                      Keep as my default for this float
                    </label>
                  )}
                </div>

                {/* Reference */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Reference</label>
                  <input value={reference} onChange={e => setReference(e.target.value)}
                    disabled={!isEditable} placeholder="Optional" className={fieldClass(!isEditable)} />
                </div>

                {/* Narration */}
                <div className="lg:col-span-3">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Narration</label>
                  <input value={narration} onChange={e => setNarration(e.target.value)}
                    disabled={!isEditable} placeholder={`Describe what this ${meta.label.toLowerCase()} is for…`}
                    className={fieldClass(!isEditable)} />
                </div>
              </div>
            </div>

            {/* ── Expenses (ML-13) ────────────────────────────────────────────
                One voucher, one approval, several small expenses — which is the
                point of the requirement. Shown only for a payment: a top-up or a
                return moves money against one funding account and has nothing to
                break down. */}
            {isItemised && (
              <div className="mt-5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-700">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Expenses</h3>
                  <span aria-label="Expenses total" className="font-mono text-sm text-slate-700 dark:text-slate-200">
                    {linesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="divide-y divide-slate-100 dark:divide-slate-700">
                  {lines.map((line, index) => (
                    <div key={line.key} className="grid grid-cols-1 lg:grid-cols-12 gap-3 px-5 py-3 items-end">
                      <div className="lg:col-span-4">
                        <AccountLookupField
                          label={index === 0 ? 'Expense Account *' : ''}
                          value={line.accountId}
                          displayCode={line.accountCode}
                          displayName={line.accountName}
                          onChange={(cid, code, name) =>
                            updateLine(line.key, { accountId: cid, accountCode: code, accountName: name })}
                          disabled={!isEditable}
                          isPostingAccount
                          placeholder={isEditable ? 'Click to select account' : '—'}
                        />
                      </div>

                      <div className="lg:col-span-2">
                        {index === 0 && (
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                            Amount *
                          </label>
                        )}
                        <input
                          type="number" min="0" step="0.01"
                          value={line.amount}
                          onChange={e => updateLine(line.key, { amount: e.target.value })}
                          disabled={!isEditable}
                          placeholder="0.00"
                          aria-label={`Expense amount ${index + 1}`}
                          className={cn(fieldClass(!isEditable), 'font-mono text-right')}
                        />
                      </div>

                      <div className="lg:col-span-3">
                        {index === 0 && (
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                            What it was for
                          </label>
                        )}
                        <input
                          value={line.description}
                          onChange={e => updateLine(line.key, { description: e.target.value })}
                          disabled={!isEditable}
                          placeholder="Taxi to the bank"
                          aria-label={`Expense description ${index + 1}`}
                          className={fieldClass(!isEditable)}
                        />
                      </div>

                      {/* Per line, because one slip genuinely splits — a taxi for the kitchen and
                          stationery for the office leave the same box on the same voucher. Blank
                          takes the voucher's centre. */}
                      <div className="lg:col-span-2">
                        {index === 0 && (
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                            Cost Center
                          </label>
                        )}
                        <select
                          value={line.costCenterId}
                          onChange={e => updateLine(line.key, { costCenterId: e.target.value })}
                          disabled={!isEditable}
                          aria-label={`Expense cost center ${index + 1}`}
                          className={fieldClass(!isEditable)}
                        >
                          <option value="">{voucherCcCode ? `Voucher's (${voucherCcCode})` : "— Voucher's —"}</option>
                          {costCentresForLocation(accessibleCostCenters, floatLocationId).map(cc => (
                            <option key={cc.id} value={cc.id}>{cc.code}</option>
                          ))}
                        </select>
                      </div>

                      <div className="lg:col-span-1 flex justify-end">
                        {isEditable && lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(line.key)}
                            aria-label={`Remove expense ${index + 1}`}
                            className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {isEditable && (
                  <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-700">
                    <button
                      type="button"
                      onClick={addLine}
                      className="flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700
                                 dark:text-amber-400 dark:hover:text-amber-300"
                    >
                      <Plus size={14} /> Add Expense Line
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Workflow trail — inline, since this is a two-step approval, not the full engine */}
            {!isNew && voucher && (
              <div className="mt-5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Workflow</h3>
                <div className="space-y-2 text-sm">
                  {voucher.submittedDate && (
                    <p className="text-slate-600 dark:text-slate-300">
                      Submitted {new Date(voucher.submittedDate).toLocaleString()}
                    </p>
                  )}
                  {voucher.approvedDate && (
                    <p className="text-slate-600 dark:text-slate-300">
                      Approved {new Date(voucher.approvedDate).toLocaleString()}
                    </p>
                  )}
                  {voucher.rejectedDate && (
                    <p className="text-rose-600 dark:text-rose-400">
                      Rejected {new Date(voucher.rejectedDate).toLocaleString()}
                      {voucher.rejectionReason ? ` — ${voucher.rejectionReason}` : ''}
                    </p>
                  )}
                  {voucher.postedDate && (
                    <p className="text-emerald-600 dark:text-emerald-400">
                      Posted {new Date(voucher.postedDate).toLocaleString()}
                    </p>
                  )}
                  {voucher.cancelledDate && (
                    <p className="text-rose-600 dark:text-rose-400">
                      Cancelled {new Date(voucher.cancelledDate).toLocaleString()}
                      {voucher.cancellationReason ? ` — ${voucher.cancellationReason}` : ''}
                    </p>
                  )}
                  {voucher.reversedDate && (
                    <p className="text-amber-600 dark:text-amber-400">
                      Reversed {new Date(voucher.reversedDate).toLocaleString()}
                    </p>
                  )}
                  {!voucher.submittedDate && (
                    <p className="text-slate-400">Still a draft — nothing has happened yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          voucher && <div className="p-6"><AuditViewer entityName="PettyCashVoucher" entityId={voucher.id} /></div>
        )}
      </div>

      {modal === 'reject' && (
        <ActionModal title="Reject Voucher" placeholder="Reason for rejection…" required
          onConfirm={reason => runAction(() => pettyCashVoucherApi.reject(voucher!.id, reason), 'Voucher rejected.')}
          onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ActionModal title="Cancel Voucher" placeholder="Reason (optional)…" required={false}
          onConfirm={reason => runAction(() => pettyCashVoucherApi.cancel(voucher!.id, reason || null), 'Voucher cancelled.')}
          onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ActionModal title="Reverse Voucher"
          placeholder={`This creates a ${MOVEMENT_META[voucher ? oppositeOf(voucher.movementType) : 'RETURN'].label.toLowerCase()} that undoes it. Reason (optional)…`}
          required={false}
          onConfirm={reason => runAction(
            () => pettyCashVoucherApi.reverse(voucher!.id, new Date().toISOString(), new Date().toISOString(), reason || null),
            'Voucher reversed.')}
          onCancel={() => setModal(null)} />
      )}

      {voucher && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Petty Cash Voucher ${voucher.voucherNumber}`}
          fileBaseName={voucher.voucherNumber}
        >
          <PettyCashVoucherPrint voucher={voucher} />
        </MasterReportPreview>
      )}
    </div>
  );
};

function oppositeOf(t: PettyCashMovementType): PettyCashMovementType {
  return t === 'TOPUP' ? 'RETURN' : t === 'RETURN' ? 'TOPUP' : 'TOPUP';
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { message?: string; errors?: { message: string }[] } } };
  return err.response?.data?.errors?.[0]?.message
      ?? err.response?.data?.message
      ?? 'Something went wrong. Please try again.';
}
